import { createContext, useContext, useEffect, useState } from 'react';
import apiService from '../Configurations/APIService';
import config from '../Configurations/APIConfig';

const TenantContext = createContext(null);

// Utility to handle JSON parsing safely
const safeJSON = (str, fallback = {}) => {
  if (!str || str === 'string' || str === 'null') return fallback;
  try { return JSON.parse(str); } catch { return fallback; }
};

// Logic to identify the corporate domain
const extractDomain = () => {
  let host = window.location.hostname.toLowerCase();
  if (host.startsWith('www.')) host = host.replace('www.', '');
  // For local development, fallback to a known corporate domain
  if (host === 'localhost' || host === '127.0.0.1') return 'b2e.myfirstbooking.in'; 
  return host;
};

export const TenantProvider = ({ children }) => {
  const [tenantData, setTenantData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTenantData = async () => {
      // 1. Check for Preview Mode (Important for Corporate Dashboard)
      const params = new URLSearchParams(window.location.search);
      const urlTemplate = params.get('template');
      const domain = extractDomain();

      if (urlTemplate) {
        sessionStorage.setItem('preview_template', urlTemplate);
      }
      const previewTemplate = urlTemplate || sessionStorage.getItem('preview_template');

      const isGenericPreview = !!previewTemplate;

      // 2. Handle Preview State (Mocking data for the UI editor)
      if (isGenericPreview) {
        setTenantData({
          templateReference: previewTemplate,
          branding: {}, theme: {}, header: [], hero: {}, footer: {},
          aboutUs: {}, contactUs: {}, seo: {}, destinations: [],
          activeModules: {}, layout: {}
        });
        setLoading(false);
        return;
      }

      // 3. Fetch Real Corporate Config from API
      try {
        const res = await apiService.getAll(`${config.GETTEMPLATEBYDOMAIN}/${domain}`);
        console.log('Corporate Domain API Response:', res);
        const data = res.data || res;

        // Clean up preview session if we found a real domain
        sessionStorage.removeItem('preview_template'); 

        setTenantData({
          companyId:         data.companyId,
          branchId:          data.branchId, 
          userId:            data.userId, 
          templateId:        data.templateId,
          domain:            data.domain,
          // Priority: URL Preview -> Database Field -> null
          templateReference: previewTemplate || data.templateReference || data.templateRefference || null,
          branding:      safeJSON(data.customBrandingJson, {}),
          theme:         safeJSON(data.styleJson, {}),
          header:        safeJSON(data.headerJson, []),
          hero:          safeJSON(data.heroJson, {}), // Corporate often uses a different hero style
          footer:        safeJSON(data.footerJson, {}),
          aboutUs:       safeJSON(data.aboutUsJson, {}),
          contactUs:     safeJSON(data.contactUsJson, {}),
          seo:           safeJSON(data.seoJson, {}),
          destinations:  safeJSON(data.destinationsJson, { destinations: [] }).destinations || [],
          activeModules: safeJSON(data.activeModulesJson, {}),
          layout:        safeJSON(data.layoutJson, {}),
          tenantId:      domain.split('.')[0] // Useful for tracking corporate subdomains
        });
      } catch (err) {
        console.error('Corporate Domain fetch failed:', err);
        // ⭐ Same as B2C: If it fails, we keep it null to trigger the "Not Configured" screen
        setTenantData({ 
            templateReference: previewTemplate || null,
            companyId: null 
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTenantData();
  }, []);

  return (
    <TenantContext.Provider value={{ tenantData, loading }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be used inside TenantProvider. Please check your App.js/index.js hierarchy.');
  }
  return ctx;
};

export default TenantContext;